/* DiaryQuickNav — sticky vertical "Quick Jump" sidebar за Хранителен дневник (desktop only).
 *
 *  Активната секция се определя през IntersectionObserver. Click → smooth-scroll
 *  до съответния DOM anchor. Скрива се на mobile (lg breakpoint).
 *
 *  Sections са декларирани в parent (DiaryPage) и подадени като prop, така че
 *  компонентът остава generic.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

export default function DiaryQuickNav({ sections }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [active, setActive] = useState(sections?.[0]?.id || null);

  // Watch which anchor is closest to viewport top (with 30% offset).
  useEffect(() => {
    if (!sections?.length) return;
    const elements = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with highest intersectionRatio that is intersecting.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        // Trigger when the section reaches the top 30% of viewport.
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const onJump = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Soft highlight pulse so user sees where they landed
      el.classList.add("ring-2", "ring-violet-400", "ring-offset-2", "transition-all");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-violet-400", "ring-offset-2");
      }, 1800);
    }
  }, []);

  if (!sections?.length) return null;

  return (
    <aside
      className="hidden lg:flex fixed left-4 top-1/2 -translate-y-1/2 z-30 flex-col gap-1 max-h-[80vh] overflow-y-auto pr-1"
      data-testid="diary-quick-nav"
      aria-label={bg ? "Бърза навигация" : "Quick navigation"}
    >
      <div
        className={`text-[9px] uppercase tracking-wider font-bold pb-1 px-2 ${
          isDark ? "text-gray-500" : "text-gray-400"
        }`}
      >
        {bg ? "Прескочи" : "Jump"}
      </div>
      {sections.map((s) => {
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onJump(s.id)}
            data-testid={`diary-quick-nav-${s.id}`}
            aria-current={isActive ? "true" : undefined}
            title={bg ? s.label_bg : s.label_en}
            className={`group flex items-center gap-2 rounded-r-full pl-2 pr-3 py-1.5 text-left transition-all border-l-2 ${
              isActive
                ? "border-violet-500 " + (isDark
                    ? "bg-violet-500/10 text-violet-300"
                    : "bg-violet-50 text-violet-700")
                : (isDark
                    ? "border-transparent text-gray-500 hover:text-gray-200 hover:bg-white/5"
                    : "border-transparent text-gray-400 hover:text-gray-700 hover:bg-slate-100")
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isActive ? "bg-violet-500 scale-125" : (isDark ? "bg-gray-700" : "bg-gray-300")
              }`}
              aria-hidden="true"
            />
            <span className="text-[11px] font-semibold whitespace-nowrap max-w-[140px] truncate">
              {bg ? s.label_bg : s.label_en}
            </span>
          </button>
        );
      })}
    </aside>
  );
}
